'use client'

import React, { useState, useEffect } from 'react'
import Image from 'next/image'
import styles from './styles.module.css'
import { processStrapiTextTags } from '@/components/StrapiContentRenderer'
import { convertRichTextToHTML } from '@/components/StrapiContentRenderer'
import { getStrapiImageUrl } from '@/lib/strapi'

interface CardsGridItem {
  id?: number
  icon?: {
    url?: string
    alternativeText?: string
    data?: {
      attributes?: {
        url?: string
        alternativeText?: string
      }
    }
  } | string
  title?: string | any
  description?: string | any
}

interface CardsGridData {
    cards_grid_items?: Array<CardsGridItem>
}

interface CardsGridProps {
  data: CardsGridData
}

const processText = (text: string | any): string => {
  if (!text) return ''
  
  if (typeof text === 'string') {
    return processStrapiTextTags(text)
  }
  
  if (Array.isArray(text)) {
    const html = convertRichTextToHTML(text)
    return processStrapiTextTags(html)
  }
  
  return processStrapiTextTags(String(text))
}

const CardsGrid: React.FC<CardsGridProps> = ({ data }) => {
  const [isMobile, setIsMobile] = useState(false)
  
  const items = data.cards_grid_items?.filter(
    item => item.title || item.description
  ) || []

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth
      setIsMobile(width <= 767)
    }
    
    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  if (items.length === 0) {
    return null
  }

  return (
    <div className={styles.rolesGrid}>
      {items.map((item, index) => {
        const title = processText(item.title)
        const description = processText(item.description)

        // Обрабатываем icon как медиа-файл из Strapi
        let iconUrl = ''
        let iconAlt = ''
        if (item.icon) {
          if (typeof item.icon === 'string') {
            iconUrl = getStrapiImageUrl(item.icon)
          } else if (item.icon.data) {
            // Обработка вложенной структуры data
            if (Array.isArray(item.icon.data) && item.icon.data.length > 0) {
              // Если data - массив
              const firstItem = item.icon.data[0] as any
              if (firstItem.attributes?.url) {
                iconUrl = getStrapiImageUrl(firstItem.attributes.url)
                iconAlt = firstItem.attributes.alternativeText || ''
              } else if (firstItem.url) {
                iconUrl = getStrapiImageUrl(firstItem.url)
                iconAlt = firstItem.alternativeText || ''
              }
            } else {
              const iconData = item.icon.data as any
              if (iconData.attributes?.url) {
                // Если data - объект с attributes
                iconUrl = getStrapiImageUrl(iconData.attributes.url || '')
                iconAlt = iconData.attributes.alternativeText || ''
              } else if (iconData.url) {
                // Если data - объект с url напрямую
                iconUrl = getStrapiImageUrl(iconData.url)
                iconAlt = iconData.alternativeText || ''
              }
            }
          } else if (item.icon.url) {
            // Если icon - объект с url напрямую
            iconUrl = getStrapiImageUrl(item.icon.url)
            iconAlt = item.icon.alternativeText || ''
          }
        }

        if (!title && !description) return null

        return (
          <div key={item.id || index} className={styles.roleCard}>
            {iconUrl && (
              <div className={styles.roleIcon}>
                <Image 
                  src={iconUrl}
                  alt={iconAlt || title || ''}
                  width={isMobile ? 32 : 40}
                  height={isMobile ? 32 : 40}
                />
              </div>
            )}
            {title && (
              <h3 
                className={styles.roleTitle}
                dangerouslySetInnerHTML={{ __html: title }}
              />
            )}
            {description && (
              <p 
                className={styles.roleDescription}
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

CardsGrid.displayName = 'CardsGrid'

export default CardsGrid

